"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from "react";
import { APP_SHELL_APP, APP_SHELL_ATTR } from "@/lib/app-shell";

/**
 * Is this document running inside an installed app shell (TWA / WKWebView /
 * installed PWA) rather than a browser tab?
 *
 * WHY A HOOK WHEN CSS CAN READ THE ATTRIBUTE. `[data-app-shell="app"]` in a
 * stylesheet is the right tool and should stay the default. This exists for the same
 * narrow case `useIsDesktopViewport` exists for: where the two shells are
 * different COMPOSITIONS rather than the same element restyled. `NFTBazDialog`
 * is exactly that — a centred dialog and a bottom sheet are two geometries, and
 * expressing the sheet as `app:max-md:` overrides would mean neutralising every
 * width class its ~30 call sites pass in (`max-w-md`, `sm:max-w-[425px]`,
 * `h-[85dvh] sm:h-[600px]`) with a matching counter-class, and silently
 * regressing the day a call site adds one nobody anticipated.
 *
 * HYDRATION, AND THE ONE RULE FOR USING THIS. It reports `false` until the
 * mount effect runs, so the server and the first client render always agree.
 * That is only safe for a surface that renders NOTHING in its closed state —
 * true of `NFTBazDialog`, which returns `null` unless `open`. Do NOT use it to
 * pick between two things that are both visible on first paint: that would
 * flash the browser presentation on every app load, which is precisely why
 * `Navbar`'s contextual header and `StickyActionBar` use the CSS variant
 * instead of this hook.
 *
 * IT OBSERVES THE ATTRIBUTE RATHER THAN READING IT ONCE. `AppShellProbe`
 * publishes the verdict from an effect, and the pre-paint snippet only applies
 * an already-remembered one — so on a first-ever launch the attribute can
 * settle AFTER a component using this has mounted. Observing removes that
 * ordering question entirely instead of depending on effect order between two
 * unrelated components.
 */
export function useIsAppShell() {
  const $ = _c(2);
  const [isAppShell, setIsAppShell] = useState(false);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      const root = document.documentElement;
      const read = () => setIsAppShell(root.getAttribute(APP_SHELL_ATTR) === APP_SHELL_APP);
      read();
      const observer = new MutationObserver(read);
      observer.observe(root, {
        attributes: true,
        attributeFilter: [APP_SHELL_ATTR]
      });
      return () => observer.disconnect();
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  return isAppShell;
}
export default useIsAppShell;